import React, { useState } from "react";

import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useClose } from "@headlessui/react";
import { twJoin } from "tailwind-merge";

import { DecisionEnvironment } from "src/api/types";
import { ConditionRenderContext } from "src/base-components/FilterPopover/types";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { FallbackInput } from "src/cases/common/FallbackInput";
import { ResourceSearchPane } from "src/datasets/components/ResourceSearchPane";
import { IconButton } from "src/design-system/Icon";
import { FIELD_BORDER } from "src/design-system/utils/fieldStyles";
import { useEntitySearch } from "src/entities/useEntitySearch";
import { useEnvironment } from "src/hooks/useEnvironment";
import { ErrorBoundary } from "src/utils/ErrorBoundary";

type RefFieldMeta = { entityType?: string };

export const EntityRefFilterValueRenderer: React.FC<ConditionRenderContext> = (
  props,
) => (
  <ErrorBoundary
    fallbackErrorContent={
      <FallbackInput
        placeholder="Search failed, please enter Entity ID manually"
        onCommit={props.onChange}
      />
    }
  >
    <EntityRefFilterValueRendererInner {...props} />
  </ErrorBoundary>
);

const EntityRefFilterValueRendererInner: React.FC<ConditionRenderContext> = ({
  groupIndex,
  conditionIndex,
  field,
  value,
  onChange,
  error,
}) => {
  const entityType = (field.meta as RefFieldMeta | undefined)?.entityType ?? "";
  const [searchQuery, setSearchQuery] = useState("");
  const [pageEnv] = useEnvironment();
  const env = pageEnv as DecisionEnvironment;

  const {
    items,
    isLoading,
    displayName,
    primaryPropertyDisplayName,
    selectedDisplayValue,
    entityIcon,
  } = useEntitySearch({
    entityType,
    searchQuery,
    selectedValue: value,
  });

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <CustomPopover
      button={
        <div
          className={twJoin(
            "flex h-8 max-w-full cursor-pointer items-center overflow-hidden rounded-lg border bg-white px-3 text-left font-inter-normal-12px focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
            !error && FIELD_BORDER.default,
            !!error && FIELD_BORDER.errored,
          )}
          data-loc={`group-${groupIndex}-condition-${conditionIndex}-value`}
          tabIndex={0}
        >
          {value ? (
            <div className="flex min-w-0 items-center gap-1">
              <div className="min-w-0 flex-1">
                <Pill size="sm" variant="gray" fullWidth>
                  {entityIcon && <Pill.Icon icon={entityIcon} />}
                  <Pill.Text>{selectedDisplayValue || value}</Pill.Text>
                </Pill>
              </div>
              <div className="shrink-0">
                <IconButton icon={faTimes} size="xs" onClick={handleClear} />
              </div>
            </div>
          ) : (
            <span className="text-gray-500">
              {entityType ? `Search ${entityType}` : "Search Entity"}
            </span>
          )}
        </div>
      }
      placement="bottom-start"
      wrapperClassName="w-0 min-w-full"
    >
      <SearchDropdownContent
        displayName={displayName}
        env={env}
        isLoading={isLoading}
        items={items}
        primaryPropertyDisplayName={primaryPropertyDisplayName}
        searchQuery={searchQuery}
        onChange={onChange}
        onSearchQueryChange={setSearchQuery}
      />
    </CustomPopover>
  );
};

const SearchDropdownContent: React.FC<{
  env: DecisionEnvironment;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  isLoading: boolean;
  items: { label: string; key: string; isFetching: boolean }[];
  displayName: string;
  primaryPropertyDisplayName: string | undefined;
  onChange: (id: string) => void;
}> = ({
  env,
  searchQuery,
  onSearchQueryChange,
  isLoading,
  items,
  displayName,
  primaryPropertyDisplayName,
  onChange,
}) => {
  const close = useClose();

  return (
    <div className="w-96">
      <ResourceSearchPane
        filters={{
          environment: env,
          searchQuery,
        }}
        hideEnvironmentFilter
        isLoading={isLoading}
        items={items}
        resourceName={displayName}
        searchPlaceholder={
          primaryPropertyDisplayName
            ? `Search by ID or ${primaryPropertyDisplayName}`
            : "Search by ID"
        }
        onChangeFilters={(filters) => {
          onSearchQueryChange(filters.searchQuery);
        }}
        onSelect={(item) => {
          onChange(item.key);
          close();
        }}
      />
    </div>
  );
};
